import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DineInThemeShell } from '../components/DineInThemeShell';
import { HallPaymentSuccess } from '../components/HallPaymentSuccess';
import { useDineInMenuTheme } from '../hooks/useDineInMenuTheme';
import { hallApi, type HallPaymentStatus } from '../services/hallApi';
import { dineInMenuQueryOptions } from '../queries/menu';
import {
  buildCaseHallTable,
  buildCasePaidPayment,
  isHallCasePaid,
  isHallCaseTipped,
} from '../data/hallCaseMock';

export default function HallPaymentReturnScreen() {
  const { theme, tokens, fadeMs } = useDineInMenuTheme();
  const [params] = useSearchParams();
  const pid = params.get('pid') || params.get('hall');
  const next = params.get('next') || '/menu';
  const fail = params.get('fail');
  const casePaid = isHallCasePaid(params);
  const caseTipped = isHallCaseTipped(params);
  const caseMock = casePaid || caseTipped;
  const { data: categories = [] } = useQuery({ ...dineInMenuQueryOptions(), enabled: caseMock });
  const casePayment = useMemo(() => {
    if (!caseMock) return null;
    return buildCasePaidPayment(buildCaseHallTable(categories), {
      kind: caseTipped ? 'tip' : 'bill',
    });
  }, [caseMock, caseTipped, categories]);
  const [status, setStatus] = useState<'wait' | 'paid' | 'fail'>(
    fail ? 'fail' : caseMock ? 'paid' : 'wait',
  );
  const [payment, setPayment] = useState<HallPaymentStatus | null>(null);
  const back = next.startsWith('/') ? next : '/menu';
  const shownPayment = caseMock ? casePayment : payment;

  useEffect(() => {
    if (caseMock || !pid || fail) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > 60) {
        setStatus('fail');
        return;
      }
      try {
        let row = await hallApi.getPayment(pid);
        if (row.status === 'paid' && row.iikoOrderId && !(row.items && row.items.length)) {
          try {
            const table = await hallApi.getOrder(row.iikoOrderId);
            row = { ...row, items: table.order?.items ?? [], waiterName: table.order?.waiterName ?? row.waiterName };
          } catch {
            /* состав из iiko опционален */
          }
        }
        setPayment(row);
        if (row.status === 'paid') {
          setStatus('paid');
          return;
        }
        if (row.status === 'failed') {
          setStatus('fail');
          return;
        }
      } catch {
        /* retry */
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [pid, fail, caseMock]);

  const menuTo = shownPayment?.tableId
    ? caseMock
      ? '/menu?case=table20'
      : `/menu?table=${encodeURIComponent(shownPayment.tableId)}`
    : back;

  return (
    <DineInThemeShell theme={theme} fadeMs={fadeMs}>
      {status === 'paid' && shownPayment ? (
        <HallPaymentSuccess payment={shownPayment} tokens={tokens} />
      ) : (
        <div
          className="min-h-screen flex items-center justify-center px-6"
          style={{ color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
        >
          <div className="max-w-sm w-full text-center space-y-4">
            {status === 'wait' ? <p>Подтверждаем оплату…</p> : null}
            {status === 'fail' ? <p>Не удалось подтвердить оплату.</p> : null}
            <Link to={menuTo} className="inline-block underline" style={{ color: tokens.muted }}>
              Вернуться в меню
            </Link>
          </div>
        </div>
      )}
    </DineInThemeShell>
  );
}
